/**
 * Провайдер аутентификации.
 *
 * Экраны входа и регистрации работают только с этим интерфейсом и ничего не
 * знают о том, где проверяются учётные данные. Пока сервер не подключён,
 * используется `localAuthProvider` (проверка в IndexedDB устройства). Когда
 * появится адрес API, достаточно реализовать `remoteAuthProvider` и поменять
 * одну строку в `getAuthProvider()` — экраны, формы и валидация останутся
 * нетронутыми.
 */

import { db } from '@/shared/db/database';
import type { User } from '@/shared/db/schema';
import { hashPassword, verifyPassword } from '@/shared/lib/crypto';
import { createId } from '@/shared/lib/id';

export interface Credentials {
  email: string;
  password: string;
}

export interface RegisterInput extends Credentials {
  name: string;
}

export interface AuthSession {
  user: User;
  /** Токен доступа. В локальном режиме — синтетический. */
  token: string;
  expiresAt: number | null;
}

export type AuthErrorCode = 'invalid_credentials' | 'email_taken' | 'not_found' | 'network';

export class AuthError extends Error {
  readonly code: AuthErrorCode;

  constructor(message: string, code: AuthErrorCode) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

export interface AuthProvider {
  login(credentials: Credentials): Promise<AuthSession>;
  register(input: RegisterInput): Promise<AuthSession>;
  logout(): Promise<void>;
  /** Восстановление сессии при запуске приложения. */
  restore(token: string): Promise<AuthSession | null>;
  /**
   * Восстановление сессии без сети.
   *
   * Нужно удалённому провайдеру: запуск без связи не должен выкидывать человека
   * из офлайн-приложения, где все данные лежат на устройстве. Локальному
   * провайдеру не нужно — у него и обычное восстановление идёт из базы.
   */
  restoreOffline?(token: string): Promise<AuthSession | null>;
}

/**
 * Сервер прямо отказал в сессии.
 *
 * Отличать этот случай от обрыва связи обязательно: отказ означает, что токен
 * недействителен и его надо снять, а обрыв — что мы просто не дозвонились.
 */
export function isRejectedSession(error: unknown): boolean {
  if (error instanceof AuthError) return true;
  const status = (error as { status?: number } | null)?.status;
  return status === 401 || status === 403;
}

/** Палитра аватаров — цвет назначается по email, чтобы был стабильным. */
const AVATAR_COLORS = [
  'var(--sphere-career)',
  'var(--sphere-health)',
  'var(--sphere-growth)',
  'var(--sphere-joy)',
  'var(--sphere-family)',
  'var(--sphere-social)',
];

function pickAvatarColor(email: string): string {
  const sum = [...email].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length]!;
}

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/**
 * Локальный провайдер: учётные записи хранятся на устройстве.
 * Позволяет полноценно пользоваться приложением до появления бэкенда.
 */
export const localAuthProvider: AuthProvider = {
  async login({ email, password }) {
    const normalized = normalizeEmail(email);
    const user = await db.users.where('email').equals(normalized).first();

    if (!user || !(await verifyPassword(password, user.passwordHash))) {
      // Одинаковый текст для «нет пользователя» и «неверный пароль»:
      // иначе форма превращается в инструмент проверки существования аккаунтов.
      throw new AuthError('Неверная почта или пароль', 'invalid_credentials');
    }

    return { user, token: `local.${user.id}`, expiresAt: null };
  },

  async register({ email, password, name }) {
    const normalized = normalizeEmail(email);
    const existing = await db.users.where('email').equals(normalized).first();

    if (existing) {
      /*
        Учётная запись уже есть — но это не всегда чужая почта.

        Регистрация состоит из двух шагов: завести запись и создать профиль с
        режимом дня. Сорвись второй шаг — запись остаётся, а профиля нет, и
        человек попадает в ловушку: «аккаунт уже существует» на ту самую почту,
        которую он только что вводил впервые. Завести новый профиль после этого
        нечем.

        Поэтому при верном пароле регистрация продолжается с того места, где
        оборвалась. Чужой аккаунт это не открывает: нужен его пароль, то есть
        ровно то же, что и для входа.
      */
      if (await verifyPassword(password, existing.passwordHash)) {
        return { user: existing, token: `local.${existing.id}`, expiresAt: null };
      }

      throw new AuthError('Аккаунт с такой почтой уже существует', 'email_taken');
    }

    const user: User = {
      id: createId(),
      email: normalized,
      name: name.trim() || normalized.split('@')[0] || 'Пользователь',
      passwordHash: await hashPassword(password),
      createdAt: Date.now(),
      avatarColor: pickAvatarColor(normalized),
    };

    await db.users.add(user);
    return { user, token: `local.${user.id}`, expiresAt: null };
  },

  async logout() {
    // Локальному режиму нечего инвалидировать на сервере.
  },

  async restore(token) {
    if (!token.startsWith('local.')) return null;
    const userId = token.slice('local.'.length);
    const user = await db.users.get(userId);
    return user ? { user, token, expiresAt: null } : null;
  },
};

let activeProvider: AuthProvider = localAuthProvider;

/**
 * Возвращает активный провайдер.
 *
 * Здесь появится ветвление, когда будет известен адрес сервера:
 * ```ts
 * return import.meta.env.VITE_API_URL ? remoteAuthProvider : localAuthProvider;
 * ```
 */
export function getAuthProvider(): AuthProvider {
  return activeProvider;
}

/**
 * Подменяет провайдер.
 *
 * Нужен там, где важно поведение приложения при разных ответах сервера:
 * запуск без сети, отказ в сессии, истёкший токен. Проверить это на локальном
 * провайдере нельзя — он всегда отвечает из базы и никогда не падает, а
 * именно на падениях приложение и вело себя неверно.
 */
export function setAuthProvider(provider: AuthProvider): void {
  activeProvider = provider;
}

/** Возврат к провайдеру по умолчанию — после тестов и при выходе из аккаунта. */
export function resetAuthProvider(): void {
  activeProvider = localAuthProvider;
}
